import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { firestore } from '../Firebase';
import HeaderPublic from './HeaderPublic';

const Page = () => {
  const { pageID } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const rootRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const fetchPage = async () => {
      try {
        const doc = await firestore.collection('pages').doc(pageID).get();
        if (mounted) {
          if (doc.exists) {
            const data = doc.data();
            // Handle migration/aliasing of content to html
            setPageData({
              ...data,
              html: data.html || data.content || ''
            });
          } else {
            console.error('Page not found');
          }
        }
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (pageID) {
      fetchPage();
    }

    return () => {
      mounted = false;
    };
  }, [pageID]);

  // Handle JS and CSS Injection
  useEffect(() => {
    if (!pageData || !rootRef.current) return;

    // Inject CSS
    let styleElement = null;
    if (pageData.css) {
      styleElement = document.createElement('style');
      styleElement.textContent = pageData.css;
      // Append to the root element's parent or document head, but scoped if possible? 
      // For global styles, head is better. For scoped, we'd need Shadow DOM or unique IDs.
      // Given the requirement for "completely custom", standard head injection is standard practice here
      // but we'll try to scope it by not over-writing global styles unless intended.
      document.head.appendChild(styleElement);
    }

    // Execute JS
    if (pageData.js) {
      try {
        // Execute the script with access to the root element
        // We use new Function to create a scoped execution environment
        // The script can return a cleanup function
        const executeScript = new Function('rootElement', pageData.js);
        const result = executeScript(rootRef.current);
        
        if (typeof result === 'function') {
          cleanupRef.current = result;
        }
      } catch (error) {
        console.error('Error executing custom script:', error);
      }
    }

    return () => {
      // Cleanup CSS
      if (styleElement) {
        document.head.removeChild(styleElement);
      }

      // Cleanup JS
      if (cleanupRef.current) {
        try {
          cleanupRef.current();
        } catch (error) {
          console.error('Error in custom script cleanup:', error);
        }
        cleanupRef.current = null;
      }
    };
  }, [pageData]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!pageData) {
    return <div>Page not found.</div>;
  }

  // If hideHeader is true, we don't render the standard layout structure with Container/Row/HeaderPublic
  // Instead we render a raw div that the user can fully control
  if (pageData.hideHeader) {
    return (
      <div className="custom-page-root" ref={rootRef}>
        {/* Optional: Add a hidden HeaderPublic if they want to use it via their own CSS/JS? 
            But the user asked to "control the Header's positioning/presence".
            If we hide it here, they can't position it.
            But the requirement was "hide the default HeaderPublic so I can position it myself via CSS".
            This implies it should still be in the DOM, or they will create their own.
            
            Re-reading the plan: "Conditionally render HeaderPublic based on hideHeader flag."
            This usually means "don't render it at all".
            
            If they want to position it themselves, they likely mean they want to render their OWN header HTML, 
            or they want the HeaderPublic to be available but hidden by default?
            
            Let's stick to the simpler interpretation: If hideHeader is on, we don't render the standard React HeaderPublic.
            The user can add their own header HTML into the HTML tab.
        */}
        <div dangerouslySetInnerHTML={{ __html: pageData.html }} />
      </div>
    );
  }

  return (
    <Container fluid>
      <Row>
        <HeaderPublic />
      </Row>
      <Row>
        <Col>
          <h1>{pageData.title}</h1>
          <div ref={rootRef} dangerouslySetInnerHTML={{ __html: pageData.html }} />
        </Col>
      </Row>
    </Container>
  );
};

export default Page;
